import { useQuery } from '@tanstack/react-query';
import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { queryKeys } from './query-client';
import { fetchWorkspaces } from './workspaces';
import { fetchWorkspaceFile, fetchWorkspaceFiles } from './workspace-files';
import { resolveVfsContent, workspaceFileRefOf } from './vfs-attachment';
import { resolveAttachmentContentState } from './attachment-content-state';

export interface AttachmentContent {
  /** What the doc viewer / open+download helpers consume: the attachment's URL, or the resolved workspace
   *  file body for a `vfsPath` artifact. Empty when there is nothing (yet) to show. */
  content: string;
  /** True while a `vfsPath` is still resolving (workspaces / workspace-files / the scoped fallback loading)
   *  — the caller keeps the chip in a pending state rather than flashing an "unavailable". */
  pending: boolean;
  /** True when a `vfsPath` artifact could NOT be resolved to any content — neither the workspace-files list
   *  nor the scoped single-file fallback returned it. The chip shows an inline error instead of a silent
   *  dead tap (see `SentAttachmentList`); `retry` re-runs the fallback. */
  error: boolean;
  /** Re-run the scoped single-file fallback — the chip's tap-to-retry. A no-op when nothing failed. */
  retry: () => void;
}

const NO_RETRY = () => {};

// Resolution reads the SHARED React Query caches directly (`queryKeys.workspaces` / `.workspaceFiles`)
// rather than the `useWorkspaces()` / `useWorkspaceFiles()` hooks, on purpose: those hooks each open a
// duplexer WEBSOCKET per call (`useFileSystemDuplexer` etc.), and a chat can render many attachment chips
// — one socket per chip is a leak. A chip only needs to READ the already-loaded list (the sidebar's
// `useWorkspaces` keeps it fresh); it doesn't need its own live subscription. Same key → React Query
// dedupes, so a plain-URL attachment (workspace-files disabled) adds no request and no socket.

/** The source for one attachment. A URL attachment IS its url. An agent-authored artifact carried by
 *  `vfsPath` (url empty) is resolved against the workspace's own files — see `vfs-attachment.ts` for why
 *  and how, and the WEB-ONLY caveat.
 *
 *  When that whole-file-system resolution comes back EMPTY — the list read is heavy or incomplete on a
 *  large account, so the file isn't in it — the hook falls back to a SCOPED single-file read
 *  (`/api/workspace-file`, `fetchWorkspaceFile`) that fetches just this one file by its path. Only when
 *  that ALSO returns nothing is the attachment `error`: the chip stops being a silent dead tap and offers
 *  a retry. */
export function useAttachmentContent(attachment: MessageAttachment): AttachmentContent {
  const url = attachment.url?.trim() ?? '';
  const vfsPath = attachment.vfsPath ?? '';
  const ref = url ? null : vfsPath ? workspaceFileRefOf(vfsPath) : null;

  const workspaces = useQuery({ queryKey: queryKeys.workspaces, queryFn: fetchWorkspaces, enabled: !!ref });
  const workspaceId = ref ? workspaces.data?.find((w) => w.slug === ref.slug)?.id : undefined;
  const files = useQuery({
    queryKey: queryKeys.workspaceFiles(workspaceId),
    queryFn: () => fetchWorkspaceFiles(workspaceId as string),
    enabled: !!workspaceId,
  });

  // The whole-file-system resolution, and whether it has SETTLED (loaded, to a hit or a miss).
  let listContent = '';
  let listPending = false;
  if (ref) {
    if (workspaces.isLoading) listPending = true;
    else if (workspaceId && files.isLoading) listPending = true;
    else if (workspaceId && files.data) listContent = resolveVfsContent(vfsPath, files.data) ?? '';
    // else: workspaces settled with no matching id, or files settled empty — a miss, not pending.
  }

  // The scoped single-file fallback, run only once the list resolution has settled to EMPTY. Keyed on the
  // vfsPath so it is its own cache entry; disabled entirely for a URL attachment or an unresolvable ref.
  const needFallback = !!ref && !listPending && !listContent;
  const fallback = useQuery({
    queryKey: queryKeys.workspaceFile(vfsPath),
    queryFn: () => fetchWorkspaceFile(vfsPath),
    enabled: needFallback,
  });

  const state = resolveAttachmentContentState({
    url,
    hasRef: !!ref,
    listPending,
    listContent,
    fallbackPending: fallback.isLoading || fallback.isFetching,
    fallbackContent: fallback.data ?? '',
  });
  // Retry is offered whenever the chip is showing (or heading toward) an error — the scoped fallback is the
  // thing to re-run; a resolved chip gets a no-op.
  const retry = state.error || (needFallback && !state.content) ? () => void fallback.refetch() : NO_RETRY;
  return { ...state, retry };
}
